/**
 * Density — Esri's Point Density / Line Density, browser-side.
 *
 * A real kernel density surface is raster work this app has no engine for
 * (see server_connections_router.py's own docstring on why nothing here
 * proxies GIS compute either). What IS available client-side is a canvas
 * heatmap (`leaflet.heat`): every input point spreads a soft circle of
 * influence, and overlapping circles add up into hot spots — visually the
 * same idea a kernel density raster shows, at the cost of an honest pixel
 * radius rather than a geodesic one (see `HeatmapConfig`'s own doc comment).
 *
 * POINT layers feed it directly, one input point per feature (optionally
 * weighted by a numeric field — a substation layer weighted by `CAPACITY_MW`
 * shows load concentration, not just substation count).
 *
 * LINE layers have no direct equivalent: a road or a pipe is not a point.
 * The stand-in here is the same one a desktop GIS uses internally before it
 * rasterises a line density surface — sample the line at a fixed spacing and
 * feed those sample points into the same point-density kernel. A longer line
 * contributes more samples, which is exactly the "more line here → denser"
 * effect Line Density is for. It is an approximation, stated as one rather
 * than presented as ArcGIS's own raster-exact answer.
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import ToolPanelHeader, { ToolActions } from '../ToolPanel';
import { cfg, scopedLayers, type ToolContext } from './types';
import { EmptyHint, Field, FieldRow, Notice, btnClass, inputClass } from './shared';
import { getLayerDisplayName } from '../../../layers/capabilities';
import { computeBounds } from '../../../layers/localFile';
import { fetchNumericFieldNames } from '../../../tools/toolFields';
import { loadFeaturesDetailed } from './featureLoad';
import type { HeatmapConfig } from '../../../types';

const EARTH_RADIUS_M = 6378137;

function distanceMetres(a: [number, number], b: [number, number]): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Target gap between line samples. A line shorter than this still gets both
 *  its endpoints, so a short service pipe is never invisible. */
const LINE_SAMPLE_SPACING_M = 40;
/** Per-feature cap, so one badly-digitised country-spanning line cannot blow
 *  the sample budget up alone — spacing widens instead of the count growing. */
const MAX_SAMPLES_PER_LINE = 400;

/** Points spread along one line's length, in flat WGS84 interpolation — good
 *  enough at sampling-interval scale, the same trade-off `centroidOf`-style
 *  helpers elsewhere in these panels already make. */
function sampleLine(coords: number[][]): [number, number][] {
  if (coords.length < 2) return coords.length === 1 ? [coords[0] as [number, number]] : [];

  let totalLength = 0;
  for (let index = 0; index < coords.length - 1; index += 1) {
    totalLength += distanceMetres(coords[index] as [number, number], coords[index + 1] as [number, number]);
  }
  if (totalLength <= 0) return [coords[0] as [number, number]];

  const spacing = Math.max(LINE_SAMPLE_SPACING_M, totalLength / MAX_SAMPLES_PER_LINE);
  const samples: [number, number][] = [];
  let travelled = 0;
  let nextMark = 0;

  for (let index = 0; index < coords.length - 1; index += 1) {
    const start = coords[index] as [number, number];
    const end = coords[index + 1] as [number, number];
    const segmentLength = distanceMetres(start, end);
    if (segmentLength <= 0) continue;

    while (nextMark <= travelled + segmentLength) {
      const t = (nextMark - travelled) / segmentLength;
      samples.push([start[0] + (end[0] - start[0]) * t, start[1] + (end[1] - start[1]) * t]);
      nextMark += spacing;
    }
    travelled += segmentLength;
  }
  samples.push(coords[coords.length - 1] as [number, number]);
  return samples;
}

/**
 * The weight-sum the busiest cluster in `points` reaches, at roughly one
 * `radiusPx`-wide grid cell — i.e. `HeatmapConfig.max`.
 *
 * Without this, `leaflet.heat` defaults `max` to 1, and every point already
 * carries a weight of 1 by default — so a single, totally isolated point
 * already paints at full intensity, and the whole layer renders as one flat
 * colour no matter how the input actually clusters (see `max`'s own doc
 * comment on `HeatmapConfig`). Binning in real ground metres, not `leaflet.heat`'s
 * own screen-pixel grid, so the estimate does not have to be redone on every
 * pan/zoom the way the library's internal one is — it only goes stale if the
 * admin zooms far enough that `radiusPx` now covers a very different ground
 * distance, which "Run" recalculates from scratch anyway.
 */
function estimateMaxDensity(
  points: { lat: number; lng: number; weight: number }[],
  radiusPx: number,
  mapZoom: number,
): number {
  if (points.length === 0) return 1;

  const avgLat = points.reduce((sum, point) => sum + point.lat, 0) / points.length;
  // Standard Web Mercator ground resolution at this latitude/zoom.
  const metresPerPixel = (156543.03392 * Math.cos((avgLat * Math.PI) / 180)) / 2 ** mapZoom;
  const cellSizeM = Math.max(1, radiusPx * metresPerPixel);
  const cellSizeDegLat = cellSizeM / 111_320;
  const cellSizeDegLng = cellSizeDegLat / Math.max(Math.cos((avgLat * Math.PI) / 180), 1e-6);

  const bins = new Map<string, number>();
  let max = 0;
  for (const point of points) {
    const key = `${Math.floor(point.lng / cellSizeDegLng)}|${Math.floor(point.lat / cellSizeDegLat)}`;
    const next = (bins.get(key) ?? 0) + point.weight;
    bins.set(key, next);
    if (next > max) max = next;
  }
  return Math.max(1, max);
}

/** Mixes a hex colour toward white — a cheap way to give a single admin-picked
 *  "peak" colour a low end without asking for two colours. */
function lighten(hex: string, amount: number): string {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return hex;
  const value = parseInt(match[1], 16);
  const mix = (channel: number) => Math.round(channel + (255 - channel) * amount);
  const r = mix((value >> 16) & 255);
  const g = mix((value >> 8) & 255);
  const b = mix(value & 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

const GRADIENT_PRESETS: Record<string, { stop: number; color: string }[]> = {
  heat: [
    { stop: 0.4, color: '#2563eb' },
    { stop: 0.6, color: '#22d3ee' },
    { stop: 0.8, color: '#facc15' },
    { stop: 1, color: '#dc2626' },
  ],
  fire: [
    { stop: 0.35, color: '#fef08a' },
    { stop: 0.65, color: '#f97316' },
    { stop: 1, color: '#b91c1c' },
  ],
  cool: [
    { stop: 0.35, color: '#e0f2fe' },
    { stop: 0.65, color: '#38bdf8' },
    { stop: 1, color: '#1d4ed8' },
  ],
  grass: [
    { stop: 0.35, color: '#fef9c3' },
    { stop: 0.65, color: '#84cc16' },
    { stop: 1, color: '#166534' },
  ],
  purple: [
    { stop: 0.35, color: '#ede9fe' },
    { stop: 0.65, color: '#a78bfa' },
    { stop: 1, color: '#5b21b6' },
  ],
  grayscale: [
    { stop: 0.3, color: '#f1f5f9' },
    { stop: 0.65, color: '#94a3b8' },
    { stop: 1, color: '#0f172a' },
  ],
};

const GRADIENT_OPTIONS: { value: string; label: string }[] = [
  { value: 'heat', label: 'Heat (blue → red)' },
  { value: 'fire', label: 'Fire (yellow → red)' },
  { value: 'cool', label: 'Cool (cyan → blue)' },
  { value: 'grass', label: 'Grass (yellow → green)' },
  { value: 'purple', label: 'Purple' },
  { value: 'grayscale', label: 'Grayscale' },
  { value: 'custom', label: 'Custom colour…' },
];

function gradientFor(preset: string, customColor: string): { stop: number; color: string }[] {
  if (preset === 'custom') {
    return [
      { stop: 0.35, color: lighten(customColor, 0.7) },
      { stop: 0.7, color: lighten(customColor, 0.25) },
      { stop: 1, color: customColor },
    ];
  }
  return GRADIENT_PRESETS[preset] ?? GRADIENT_PRESETS.heat;
}

export function DensityPanel({ ctx }: { ctx: ToolContext }) {
  const allowed = scopedLayers(ctx.config, ctx.layers);
  const defaultRadius = cfg<number>(ctx.config, 'defaultRadius', 25);
  const defaultGradient = cfg<string>(ctx.config, 'defaultGradient', 'heat');

  const [layerId, setLayerId] = useState(allowed[0]?.id ?? '');
  const [radius, setRadius] = useState(defaultRadius);
  const [weightField, setWeightField] = useState('');
  const [numericFields, setNumericFields] = useState<string[]>([]);
  const [gradientPreset, setGradientPreset] = useState(defaultGradient);
  const [customColor, setCustomColor] = useState('#dc2626');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{
    sourceFeatures: number;
    samples: number;
    skipped: number;
    truncated: boolean;
  } | null>(null);
  const [resultLayerId, setResultLayerId] = useState<string | null>(null);

  const source = allowed.find((layer) => layer.id === layerId);
  const gradient = useMemo(
    () => gradientFor(gradientPreset, customColor),
    [gradientPreset, customColor],
  );

  // A weight field only makes sense once a source is chosen, and the list
  // belongs to THAT layer — switching layers must drop a field name the new
  // one may not even have.
  useEffect(() => {
    setWeightField('');
    if (!source) {
      setNumericFields([]);
      return undefined;
    }
    let live = true;
    void fetchNumericFieldNames(source, ctx.module).then((fields) => {
      if (live) setNumericFields(Array.from(fields).sort());
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source?.id]);

  // A result already on the map (from a previous Run in this same panel
  // session) still exists as a real layer — re-running updates it in place
  // instead of stacking a fresh "Density — X" layer per click, which is what
  // makes tweaking the radius or colour and re-running feel like adjusting a
  // setting rather than repeatedly creating things to clean up after.
  const resultStillOnMap = Boolean(resultLayerId) && ctx.layers.some((layer) => layer.id === resultLayerId);

  const run = async () => {
    if (!source) return;
    setError(null);
    setBusy(true);
    try {
      const { features, truncated } = await loadFeaturesDetailed(source, ctx, { limit: 3000 });
      if (features.length === 0) {
        setSummary(null);
        setError('That layer returned no features to sample.');
        return;
      }

      const points: { lat: number; lng: number; weight: number }[] = [];
      let skipped = 0;

      for (const feature of features) {
        const geometry = feature.geometry;
        if (!geometry) {
          skipped += 1;
          continue;
        }
        const rawWeight = weightField ? Number((feature.properties ?? {})[weightField]) : NaN;
        const weight = Number.isFinite(rawWeight) && rawWeight > 0 ? rawWeight : 1;

        if (geometry.type === 'Point') {
          const [lng, lat] = geometry.coordinates as [number, number];
          points.push({ lat, lng, weight });
        } else if (geometry.type === 'MultiPoint') {
          for (const coordinate of geometry.coordinates) {
            points.push({ lat: coordinate[1], lng: coordinate[0], weight });
          }
        } else if (geometry.type === 'LineString') {
          for (const [lng, lat] of sampleLine(geometry.coordinates)) points.push({ lat, lng, weight });
        } else if (geometry.type === 'MultiLineString') {
          for (const line of geometry.coordinates) {
            for (const [lng, lat] of sampleLine(line)) points.push({ lat, lng, weight });
          }
        } else {
          skipped += 1;
        }
      }

      if (points.length === 0) {
        setSummary(null);
        setError('That layer has no point or line geometry to build a density surface from.');
        return;
      }

      const geojson: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: points.map((point) => ({
          type: 'Feature',
          properties: { weight: point.weight },
          geometry: { type: 'Point', coordinates: [point.lng, point.lat] },
        })),
      };

      const heatmap: HeatmapConfig = {
        radius,
        blur: Math.max(4, Math.round(radius * 0.6)),
        gradient,
        max: estimateMaxDensity(points, radius, ctx.mapZoom),
      };
      const name = `Density — ${getLayerDisplayName(source)}`;

      if (resultStillOnMap && resultLayerId && ctx.onLayerChange) {
        ctx.onLayerChange(resultLayerId, {
          localData: geojson,
          bounds: computeBounds(geojson),
          heatmap,
        });
      } else {
        const newId = ctx.onCreateTempLayer?.(name, geojson, 'Density', undefined, heatmap);
        setResultLayerId(newId ?? null);
      }

      setSummary({ sourceFeatures: features.length, samples: points.length, skipped, truncated });
    } finally {
      setBusy(false);
    }
  };

  /* Clears the PANEL, not the map — the result is a layer now (see Buffer's
     own identical note), removed from the Layers tab like anything else. */
  const clearPanel = () => {
    setSummary(null);
    setError(null);
    setResultLayerId(null);
  };

  return (
    <>
      <ToolPanelHeader
        title="Density"
        description="Show where a point or line layer clusters most, as a heatmap."
        onClose={ctx.onClose}
      />

      {allowed.length === 0 ? (
        <EmptyHint>This map has no layers this tool is allowed to use yet.</EmptyHint>
      ) : (
        <>
          <Field label="Analyse this layer">
            <select value={layerId} onChange={(event) => setLayerId(event.target.value)} className={inputClass}>
              <option value="">Choose a layer…</option>
              {allowed.map((layer) => (
                <option key={layer.id} value={layer.id}>
                  {getLayerDisplayName(layer)}
                </option>
              ))}
            </select>
          </Field>

          {numericFields.length > 0 && (
            <Field
              label="Weight by"
              hint="Optional — a numeric field that makes some features count for more than one (e.g. a substation's capacity, a road's traffic count). Leave unset to weigh every feature the same."
            >
              <select
                value={weightField}
                onChange={(event) => setWeightField(event.target.value)}
                className={inputClass}
              >
                <option value="">Equal weight</option>
                {numericFields.map((field) => (
                  <option key={field} value={field}>
                    {field}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field
            label="Point radius"
            hint="How far one point's influence spreads on screen. Larger blends nearby points into broader hot spots."
          >
            <div className="flex items-center gap-2">
              <input
                type="range"
                min={5}
                max={100}
                value={radius}
                onChange={(event) => setRadius(Number(event.target.value))}
                className="flex-1"
              />
              <span className="w-10 flex-shrink-0 text-right text-[12px] tabular-nums text-slate-600">
                {radius}px
              </span>
            </div>
          </Field>

          <FieldRow>
            <Field label="Colour ramp">
              <select
                value={gradientPreset}
                onChange={(event) => setGradientPreset(event.target.value)}
                className={inputClass}
              >
                {GRADIENT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            {gradientPreset === 'custom' && (
              <Field label="Peak colour">
                <input
                  type="color"
                  value={customColor}
                  onChange={(event) => setCustomColor(event.target.value)}
                  aria-label="Peak colour"
                  className="h-8 w-full cursor-pointer rounded border border-slate-200 bg-white p-0.5"
                />
              </Field>
            )}
          </FieldRow>

          {/* A live strip of the chosen ramp, so the colour picked above is
              confirmed before spending a Run on it. */}
          <div
            className="h-2.5 w-full rounded-full"
            style={{
              background: `linear-gradient(to right, ${gradient
                .map((entry) => `${entry.color} ${Math.round(entry.stop * 100)}%`)
                .join(', ')})`,
            }}
          />

          <ToolActions>
            <button
              type="button"
              onClick={() => void run()}
              disabled={!source || busy}
              className={`${btnClass} gl-btn--primary`}
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {busy ? 'Analysing…' : resultStillOnMap ? 'Update' : 'Run'}
            </button>
            <button
              type="button"
              onClick={clearPanel}
              disabled={!summary}
              className={`${btnClass} gl-btn--secondary`}
            >
              Reset
            </button>
          </ToolActions>

          {error && <Notice tone="warn">{error}</Notice>}

          {summary && (
            <Notice>
              {summary.samples.toLocaleString()} sample point{summary.samples === 1 ? '' : 's'} from{' '}
              {summary.sourceFeatures.toLocaleString()} feature{summary.sourceFeatures === 1 ? '' : 's'}
              {summary.skipped > 0
                ? ` (${summary.skipped} skipped — not point or line geometry)`
                : ''}
              {summary.truncated ? '. The service had more than this — showing the first batch only.' : '.'}
              {' '}Added to the Layers tab as a temporary layer. It stays until you remove it, and nobody
              else sees it.
            </Notice>
          )}

          {/* Honest about the method, the same way Buffer's own footer is —
              a heatmap that looks precise and is not would be the worst
              outcome here. */}
          <p className="text-[10.5px] leading-snug text-slate-400">
            Points feed the heatmap directly. Lines are sampled every ~{LINE_SAMPLE_SPACING_M} m along
            their length as a stand-in for a true line-density raster, which this map has no engine to
            compute. The radius above is a screen distance, not a ground one — it will look larger when
            you zoom in. Hot/cold is calibrated to the busiest spot at the CURRENT view — zoom or pan a
            lot, then Update, to recalibrate.
          </p>
        </>
      )}
    </>
  );
}
